import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  Stack,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon
} from '@mui/icons-material';
import { FrameworkTemplate, SectionTemplate, SubsectionTemplate, ItemTemplate } from '../../models/types';

interface TemplateEditorProps {
  template: FrameworkTemplate;
  onSave: (template: FrameworkTemplate) => void;
  onBack: () => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  template,
  onSave,
  onBack
}) => {
  const [editedTemplate, setEditedTemplate] = useState<FrameworkTemplate>({...template});
  const [dialogType, setDialogType] = useState<'section' | 'subsection' | 'item' | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({
    sectionIndex: -1,
    subsectionIndex: -1,
    itemIndex: -1,
    isNew: true
  });
  const [editingData, setEditingData] = useState({
    name: '',
    description: '',
    defaultWeight: 1.0,
    isCore: true,
    title: '',
    isRequired: false,
    guidance: ''
  });

  const handleSave = () => {
    onSave(editedTemplate);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTemplate({
      ...editedTemplate,
      name: e.target.value
    });
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTemplate({
      ...editedTemplate,
      description: e.target.value
    });
  };

  // Section handlers
  const handleAddSection = () => {
    setDialogType('section');
    setDialogData({
      sectionIndex: -1,
      subsectionIndex: -1,
      itemIndex: -1,
      isNew: true
    });
    setEditingData({
      name: '',
      description: '',
      defaultWeight: 1.0,
      isCore: true,
      title: '',
      isRequired: false,
      guidance: ''
    });
    setDialogOpen(true);
  };

  const handleEditSection = (sectionIndex: number) => {
    const section = editedTemplate.sections[sectionIndex];
    setDialogType('section');
    setDialogData({
      sectionIndex,
      subsectionIndex: -1,
      itemIndex: -1,
      isNew: false
    });
    setEditingData({
      name: section.name,
      description: section.description,
      defaultWeight: section.defaultWeight,
      isCore: section.isCore,
      title: '',
      isRequired: false,
      guidance: ''
    });
    setDialogOpen(true);
  };

  const handleDeleteSection = (sectionIndex: number) => {
    const newSections = [...editedTemplate.sections];
    newSections.splice(sectionIndex, 1);
    setEditedTemplate({
      ...editedTemplate,
      sections: newSections
    });
  };

  const handleMoveSection = (sectionIndex: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && sectionIndex === 0) ||
      (direction === 'down' && sectionIndex === editedTemplate.sections.length - 1)
    ) {
      return;
    }

    const newSections = [...editedTemplate.sections];
    const newIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
    
    const temp = newSections[sectionIndex];
    newSections[sectionIndex] = newSections[newIndex];
    newSections[newIndex] = temp;
    
    setEditedTemplate({
      ...editedTemplate,
      sections: newSections
    });
  };

  // Subsection handlers
  const handleAddSubsection = (sectionIndex: number) => {
    setDialogType('subsection');
    setDialogData({
      sectionIndex,
      subsectionIndex: -1,
      itemIndex: -1,
      isNew: true
    });
    setEditingData({
      name: '',
      description: '',
      defaultWeight: 1.0,
      isCore: false,
      title: '',
      isRequired: false,
      guidance: ''
    });
    setDialogOpen(true);
  };

  const handleEditSubsection = (sectionIndex: number, subsectionIndex: number) => {
    const subsection = editedTemplate.sections[sectionIndex].subsections[subsectionIndex];
    setDialogType('subsection');
    setDialogData({
      sectionIndex,
      subsectionIndex,
      itemIndex: -1,
      isNew: false
    });
    setEditingData({
      name: subsection.name,
      description: subsection.description,
      defaultWeight: subsection.defaultWeight,
      isCore: false,
      title: '',
      isRequired: false,
      guidance: ''
    });
    setDialogOpen(true);
  };

  const handleDeleteSubsection = (sectionIndex: number, subsectionIndex: number) => {
    const newSections = [...editedTemplate.sections];
    newSections[sectionIndex].subsections.splice(subsectionIndex, 1);
    setEditedTemplate({
      ...editedTemplate,
      sections: newSections
    });
  };

  const handleMoveSubsection = (sectionIndex: number, subsectionIndex: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && subsectionIndex === 0) ||
      (direction === 'down' && subsectionIndex === editedTemplate.sections[sectionIndex].subsections.length - 1)
    ) {
      return;
    }

    const newSections = [...editedTemplate.sections];
    const newIndex = direction === 'up' ? subsectionIndex - 1 : subsectionIndex + 1;
    
    const temp = newSections[sectionIndex].subsections[subsectionIndex];
    newSections[sectionIndex].subsections[subsectionIndex] = newSections[sectionIndex].subsections[newIndex];
    newSections[sectionIndex].subsections[newIndex] = temp;
    
    setEditedTemplate({
      ...editedTemplate,
      sections: newSections
    });
  };

  // Item handlers
  const handleAddItem = (sectionIndex: number, subsectionIndex: number) => {
    setDialogType('item');
    setDialogData({
      sectionIndex,
      subsectionIndex,
      itemIndex: -1,
      isNew: true
    });
    setEditingData({
      name: '',
      description: '',
      defaultWeight: 1.0,
      isCore: false,
      title: '',
      isRequired: false,
      guidance: ''
    });
    setDialogOpen(true);
  };

  const handleEditItem = (sectionIndex: number, subsectionIndex: number, itemIndex: number) => {
    const item = editedTemplate.sections[sectionIndex].subsections[subsectionIndex].items[itemIndex];
    setDialogType('item');
    setDialogData({
      sectionIndex,
      subsectionIndex,
      itemIndex,
      isNew: false
    });
    setEditingData({
      name: '',
      description: item.description,
      defaultWeight: item.defaultWeight,
      isCore: false,
      title: item.title,
      isRequired: item.isRequired,
      guidance: item.guidance || ''
    });
    setDialogOpen(true);
  };

  const handleDeleteItem = (sectionIndex: number, subsectionIndex: number, itemIndex: number) => {
    const newSections = [...editedTemplate.sections];
    newSections[sectionIndex].subsections[subsectionIndex].items.splice(itemIndex, 1);
    setEditedTemplate({
      ...editedTemplate,
      sections: newSections
    });
  };

  const handleMoveItem = (
    sectionIndex: number, 
    subsectionIndex: number, 
    itemIndex: number, 
    direction: 'up' | 'down'
  ) => {
    if (
      (direction === 'up' && itemIndex === 0) ||
      (direction === 'down' && 
        itemIndex === editedTemplate.sections[sectionIndex].subsections[subsectionIndex].items.length - 1)
    ) {
      return;
    }

    const newSections = [...editedTemplate.sections];
    const newIndex = direction === 'up' ? itemIndex - 1 : itemIndex + 1;
    
    const temp = newSections[sectionIndex].subsections[subsectionIndex].items[itemIndex];
    newSections[sectionIndex].subsections[subsectionIndex].items[itemIndex] = 
      newSections[sectionIndex].subsections[subsectionIndex].items[newIndex];
    newSections[sectionIndex].subsections[subsectionIndex].items[newIndex] = temp;
    
    setEditedTemplate({
      ...editedTemplate,
      sections: newSections
    });
  };

  // Dialog submit handlers
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogSubmit = () => {
    const newSections = [...editedTemplate.sections];

    if (dialogType === 'section') {
      const newSection: SectionTemplate = {
        name: editingData.name,
        description: editingData.description,
        defaultWeight: editingData.defaultWeight,
        isCore: editingData.isCore,
        subsections: dialogData.isNew ? [] : newSections[dialogData.sectionIndex].subsections
      };

      if (dialogData.isNew) {
        newSections.push(newSection);
      } else {
        newSections[dialogData.sectionIndex] = newSection;
      }
    } else if (dialogType === 'subsection') {
      const { sectionIndex } = dialogData;
      const newSubsection: SubsectionTemplate = {
        name: editingData.name,
        description: editingData.description,
        defaultWeight: editingData.defaultWeight,
        items: dialogData.isNew ? [] : newSections[sectionIndex].subsections[dialogData.subsectionIndex].items
      };

      if (dialogData.isNew) {
        newSections[sectionIndex].subsections.push(newSubsection);
      } else {
        newSections[sectionIndex].subsections[dialogData.subsectionIndex] = newSubsection;
      }
    } else if (dialogType === 'item') {
      const { sectionIndex, subsectionIndex } = dialogData;
      const newItem: ItemTemplate = {
        title: editingData.title,
        description: editingData.description,
        defaultWeight: editingData.defaultWeight,
        isRequired: editingData.isRequired,
        guidance: editingData.guidance
      };

      if (dialogData.isNew) {
        newSections[sectionIndex].subsections[subsectionIndex].items.push(newItem);
      } else {
        newSections[sectionIndex].subsections[subsectionIndex].items[dialogData.itemIndex] = newItem;
      }
    }

    setEditedTemplate({
      ...editedTemplate,
      sections: newSections
    });
    setDialogOpen(false);
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={onBack}
        >
          Back to Templates
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<SaveIcon />} 
          onClick={handleSave}
        >
          Save Template
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Template Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Template Name"
              value={editedTemplate.name}
              onChange={handleNameChange}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={editedTemplate.description}
              onChange={handleDescriptionChange}
              fullWidth
              multiline
              rows={2}
              margin="normal"
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h2">
          Sections
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<AddIcon />} 
          onClick={handleAddSection}
        >
          Add Section
        </Button>
      </Box>

      {editedTemplate.sections.map((section, sectionIndex) => (
        <Accordion key={sectionIndex} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {section.name}
              </Typography>
              <Box>
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation();
                  handleMoveSection(sectionIndex, 'up');
                }}>
                  <MoveUpIcon />
                </IconButton>
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation();
                  handleMoveSection(sectionIndex, 'down');
                }}>
                  <MoveDownIcon />
                </IconButton>
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation();
                  handleEditSection(sectionIndex);
                }}>
                  <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSection(sectionIndex);
                }}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {section.description}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Weight:</strong> {section.defaultWeight}
                </Typography>
                <Typography variant="body2">
                  <strong>Core:</strong> {section.isCore ? 'Yes' : 'No'}
                </Typography>
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Subsections
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<AddIcon />} 
                onClick={() => handleAddSubsection(sectionIndex)}
              >
                Add Subsection
              </Button>
            </Box>

            {section.subsections.map((subsection, subsectionIndex) => (
              <Accordion key={subsectionIndex} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                    <Typography>
                      {subsection.name}
                    </Typography>
                    <Box>
                      <IconButton size="small" onClick={(e) => {
                        e.stopPropagation();
                        handleMoveSubsection(sectionIndex, subsectionIndex, 'up');
                      }}>
                        <MoveUpIcon />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => {
                        e.stopPropagation();
                        handleMoveSubsection(sectionIndex, subsectionIndex, 'down');
                      }}>
                        <MoveDownIcon />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => {
                        e.stopPropagation();
                        handleEditSubsection(sectionIndex, subsectionIndex);
                      }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSubsection(sectionIndex, subsectionIndex);
                      }}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {subsection.description}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Weight:</strong> {subsection.defaultWeight}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2">
                      Items
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small"
                      startIcon={<AddIcon />} 
                      onClick={() => handleAddItem(sectionIndex, subsectionIndex)}
                    >
                      Add Item
                    </Button>
                  </Box>

                  <Grid container spacing={2}>
                    {subsection.items.map((item, itemIndex) => (
                      <Grid item xs={12} md={6} lg={4} key={itemIndex}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              {item.title}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              {item.description}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" component="div">
                                <Stack direction="row" spacing={2}>
                                  <Typography variant="body2">
                                    <strong>Weight:</strong> {item.defaultWeight}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Required:</strong> {item.isRequired ? 'Yes' : 'No'}
                                  </Typography>
                                </Stack>
                              </Typography>
                              {item.guidance && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  <strong>Guidance:</strong> {item.guidance}
                                </Typography>
                              )}
                            </Box>
                          </CardContent>
                          <CardActions>
                            <IconButton size="small" onClick={() => 
                              handleMoveItem(sectionIndex, subsectionIndex, itemIndex, 'up')
                            }>
                              <MoveUpIcon />
                            </IconButton>
                            <IconButton size="small" onClick={() => 
                              handleMoveItem(sectionIndex, subsectionIndex, itemIndex, 'down')
                            }>
                              <MoveDownIcon />
                            </IconButton>
                            <IconButton size="small" onClick={() => 
                              handleEditItem(sectionIndex, subsectionIndex, itemIndex)
                            }>
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" onClick={() => 
                              handleDeleteItem(sectionIndex, subsectionIndex, itemIndex)
                            }>
                              <DeleteIcon />
                            </IconButton>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Edit Dialogs */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogType === 'section' && (dialogData.isNew ? 'Add Section' : 'Edit Section')}
          {dialogType === 'subsection' && (dialogData.isNew ? 'Add Subsection' : 'Edit Subsection')}
          {dialogType === 'item' && (dialogData.isNew ? 'Add Item' : 'Edit Item')}
        </DialogTitle>
        <DialogContent>
          {(dialogType === 'section' || dialogType === 'subsection') && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                value={editingData.name}
                onChange={(e) => setEditingData({ ...editingData, name: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Description"
                type="text"
                fullWidth
                multiline
                rows={2}
                value={editingData.description}
                onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Weight"
                type="number"
                inputProps={{ min: 0, step: 0.1 }}
                fullWidth
                value={editingData.defaultWeight}
                onChange={(e) => setEditingData({ 
                  ...editingData, 
                  defaultWeight: parseFloat(e.target.value) || 0 
                })}
              />
              {dialogType === 'section' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editingData.isCore}
                      onChange={(e) => setEditingData({ ...editingData, isCore: e.target.checked })}
                    />
                  }
                  label="Core Section"
                />
              )}
            </>
          )}
          {dialogType === 'item' && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Title"
                type="text"
                fullWidth
                value={editingData.title}
                onChange={(e) => setEditingData({ ...editingData, title: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Description"
                type="text"
                fullWidth
                multiline
                rows={2}
                value={editingData.description}
                onChange={(e) => setEditingData({ ...editingData, description: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Weight"
                type="number"
                inputProps={{ min: 0, step: 0.1 }}
                fullWidth
                value={editingData.defaultWeight}
                onChange={(e) => setEditingData({ 
                  ...editingData, 
                  defaultWeight: parseFloat(e.target.value) || 0 
                })}
              />
              <TextField
                margin="dense"
                label="Guidance"
                type="text"
                fullWidth
                multiline
                rows={3}
                value={editingData.guidance}
                onChange={(e) => setEditingData({ ...editingData, guidance: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editingData.isRequired}
                    onChange={(e) => setEditingData({ ...editingData, isRequired: e.target.checked })}
                  />
                }
                label="Required Item"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDialogSubmit} 
            color="primary"
            disabled={
              (dialogType === 'section' || dialogType === 'subsection') 
                ? !editingData.name 
                : !editingData.title
            }
          >
            {dialogData.isNew ? 'Add' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateEditor;
